/**
 * Bridges our custom add-to-cart flows to Horizon's native cart UI.
 *
 * assets/product-main-shop.js (PDP add-to-cart), assets/product-card-variants.js
 * (collection card quick add-to-cart), and assets/product-crosssell.js (bundle
 * "Add Selected to Cart") are all plain fetch('/cart/add.js') calls — they
 * deliberately don't use Horizon's native <product-form> web component (see
 * product-main-shop.js's header comment). Each already dispatches a
 * 'cart:update' CustomEvent on success, but two pieces of native cart UI only
 * react to @shopify/events' StandardEvents.cartLinesUpdate — a different,
 * more involved event carrying a resolved {cart, detail} promise, which our
 * plain scripts have no simple, verifiable way to construct — so that plain
 * event was previously going nowhere:
 *
 * 1. The header cart icon bubble (snippets/cart-bubble.liquid). Fixed here
 *    by updating its ref="cartBubble"/"cartBubbleCount" elements directly —
 *    the same elements/classes cart-icon.js's own renderCartBubble() uses.
 *
 * 2. The cart drawer (<theme-drawer id="cart-drawer">, snippets/cart-drawer.liquid),
 *    which previously never opened or refreshed after one of our custom
 *    add-to-cart flows. Each add-to-cart fetch now reads the real section id
 *    straight off the live <cart-items-component data-section-id> element
 *    and passes it as the `sections` parameter on the /cart/add.js POST body
 *    — Shopify's cart endpoints specifically support re-rendering arbitrary
 *    "sections" this way even for cart markup that lives in the layout
 *    rather than a real theme-editor section (confirmed directly against
 *    the live store: a plain GET to /cart?sections=<id> returns nothing
 *    useful, but sections requested IN THE ADD POST BODY come back fully
 *    rendered, including the item just added). The event's detail carries
 *    that response through here, where it's swapped into the DOM, and the
 *    drawer is opened by clicking the same [data-testid="cart-drawer-trigger"]
 *    button the header cart icon itself is, so the open behaviour is
 *    identical either way — not a separate, hand-rolled toggle.
 *
 * Both no-op safely if the store's cart type isn't "Drawer" (no trigger
 * button/section id exists in that case).
 */
(function () {
  'use strict';

  function refreshCartBubble() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var count = cart.item_count || 0;

        document.querySelectorAll('[ref="cartBubbleCount"]').forEach(function (el) {
          el.textContent = count > 0 && count < 100 ? String(count) : '';
          el.classList.toggle('hidden', count === 0);
        });

        document.querySelectorAll('[ref="cartBubble"]').forEach(function (el) {
          el.classList.toggle('visually-hidden', count === 0);
        });
      })
      .catch(function (error) {
        console.error('[fixplus] cart bubble refresh failed:', error);
      });
  }

  function applyCartDrawerSections(sections) {
    var trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
    var drawer = document.getElementById('cart-drawer');
    // Cart type isn't "Drawer", or the drawer markup isn't present — nothing to do.
    if (!trigger || !drawer) return;

    if (sections) {
      var current = document.querySelector('cart-items-component[data-section-id]');
      var sectionId = current && current.getAttribute('data-section-id');
      var html = sectionId && sections[sectionId];

      if (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('cart-items-component[data-section-id="' + sectionId + '"]');
        if (fresh) current.replaceWith(fresh);
      }
    }

    if (!drawer.hasAttribute('open')) trigger.click();
  }

  document.addEventListener('cart:update', function (event) {
    refreshCartBubble();
    applyCartDrawerSections(event.detail && event.detail.sections);
  });
})();
